/**
 * Automation test fixtures as the record UI reads them.
 *
 * The stored shape is the backend's `FixturePayload`
 * (apps/automations/actions/create_automation/fixtures_payload.py): the agent
 * authors these, the sandbox validates them at proposal time, and they are
 * persisted verbatim under `ahoy_script.fixtures` on approval. Nothing here is
 * rendered markup — every field is structured data the section lays out.
 *
 * `getEffectiveFixtures` is the fixtures counterpart of
 * `getEffectiveScriptSource` (./scriptSource.ts). A pending proposal's
 * fixtures are projected onto the record under the same property, so the one
 * read makes a draft reviewable before it is approved.
 */

import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

export interface FixtureExpectEvent {
  type: string;
  entity_id?: string;
  entity_type_name?: string;
  target_entity_id?: string;
  association_type_name?: string;
  properties?: Record<string, unknown>;
  form_name?: string;
  page_url?: string;
  page_title?: string;
}

export interface FixtureTriggerEvent {
  entity_type_name: string;
  event_type_name: string;
  occurred_at: string;
  payload: unknown;
}

/** A record the fixture seeds; `entity_id` is the fixture-local alias. */
export interface FixtureEntity {
  entity_id?: string | null;
  entity_type_name: string;
  properties?: Record<string, unknown>;
}

/** A record the script is expected to CREATE, named by its fixture-local alias. */
export interface FixtureReference {
  name: string;
  entity_type_name: string;
}

export interface Fixture {
  name: string;
  label?: string;
  trigger_event?: FixtureTriggerEvent | null;
  scheduled_at?: string | null;
  entities?: FixtureEntity[];
  references?: FixtureReference[];
  expect?: {
    events?: FixtureExpectEvent[];
  };
}

interface ScriptValue {
  fixtures?: unknown;
}

function isFixture(value: unknown): value is Fixture {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).name === 'string'
  );
}

/** The well-formed fixtures in an untyped list; anything without a name is dropped. */
export function parseFixtures(raw: unknown): Fixture[] {
  return Array.isArray(raw) ? raw.filter(isFixture) : [];
}

/**
 * The well-formed fixtures on a record's `ahoy_script`. A pending draft's
 * fixtures arrive the same way — projected onto the object — so there is one
 * read for both. Empty when it carries none.
 */
export function getEffectiveFixtures(object: CrmObject | undefined): Fixture[] {
  return parseFixtures(
    getPropertyValue<ScriptValue>(object?.properties?.ahoy_script)?.fixtures
  );
}
